import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { LogService } from './log.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AdminUser } from '../types/admin/admin-user';
import { Observable } from 'rxjs';
import { Organization } from '../types/organization';
import { AdminFacility } from '../types/admin/admin-facility';
import { AdminOrganization } from '../types/admin/admin-organization';
import { SessionStorageService } from './session-storage.service';

@Injectable({
  providedIn: 'root'
})
export class HsApiService {
  private api = environment.api;

  constructor(
    private log: LogService,
    private storageService: SessionStorageService,
    private http: HttpClient
  ) {}

  getOrganizations(): Observable<Organization[]> {
    const url = this.api.adminListOrganizations;
    this.log.trace('HsApiService.getOrganizations.url', url);
    return this.http.post<Organization[]>(url, {
      uuxSessionId: this.storageService.getUUXSessionId(),
      // pass username since UUA session has not been created yet
      username: this.storageService.getUser().username
    });
  }

  getFacilities(): Observable<AdminFacility[]> {
    const url = this.api.adminListFacilities;
    this.log.trace('HsApiService.getFacilities.url', url);
    return this.http.post<AdminFacility[]>(url, {
      uuxSessionId: this.storageService.getUUXSessionId(),
      // pass username since UUA session has not been created yet
      username: this.storageService.getUser().username
    });
  }

  getUsers(userPoolId: string, facilityCode?: string): Observable<AdminUser[]> {
    const url = this.api.adminListUsers;
    this.log.trace('HsApiService.getUsers.url', url);
    return this.http.post<AdminUser[]>(url, {
      userPoolId: userPoolId,
      facilityCode: facilityCode || '',
      uuxSessionId: this.storageService.getUUXSessionId()
    });
  }

  getOrganizationDetails(userPoolId: string): Observable<AdminOrganization> {
    const url = this.api.adminGetOrganizationDetails;
    this.log.trace('HsApiService.getOrganizationDetails.url', url);
    return this.http.post<AdminOrganization>(url, {
      userPoolId: userPoolId,
      uuxSessionId: this.storageService.getUUXSessionId()
    });
  }

  getUserDetails(userPoolId: string, username: string): Observable<AdminUser> {
    const url = this.api.adminGetUserDetails;
    this.log.trace('HsApiService.getUserDetails.url', url);
    return this.http.post<AdminUser>(url, {
      userPoolId: userPoolId,
      username: username,
      uuxSessionId: this.storageService.getUUXSessionId()
    });
  }

  searchUser(username: string): Observable<string> {
    const url = this.api.searchUser;
    this.log.trace('HsApiService.searchUser.url', url);
    return this.http.post<string>(url, {
      username: username,
    });
  }
  getBulkLoadPreview(
    sendWelcome: boolean,
    file: File,
    organizationId: string,
    sessionId: string
  ): Observable<any> {
    const params = {
      dryRun: 1,
      sendWelcome: sendWelcome ? 1 : 0,
      organization: organizationId
    };
    const url = this.getUrl(environment.api.adminBulkLoadUsers, params);
    const headers = new HttpHeaders().append('Authorization', sessionId);
    this.log.trace('HsApiService.getBulkLoadPreview.url', url);
    return this.http.post(url, file, { headers: headers });
  }

  doBulkUpload(
    sendWelcome: boolean,
    file: File,
    organizationId: string,
    sessionId: string
  ): Observable<any> {
    const params = {
      sendWelcome: sendWelcome ? 1 : 0,
      organization: organizationId
    };
    const url = this.getUrl(environment.api.adminBulkLoadUsers, params);
    const headers = new HttpHeaders().append('Authorization', sessionId);
    this.log.trace('HsApiService.doBulkUpload.url', url);
    return this.http.post(url, file, { headers: headers });
  }

  // User Action -------------------
  resendWelcome(userPoolId: string, username: string): Observable<any> {
    return this.userAction(userPoolId, username, 'resendWelcome');
  }

  resetPassword(userPoolId: string, username: string): Observable<any> {
    return this.userAction(userPoolId, username, 'resetPassword');
  }

  lockUser(userPoolId: string, username: string): Observable<any> {
    return this.userAction(userPoolId, username, 'lock');
  }

  unlockUser(userPoolId: string, username: string): Observable<any> {
    return this.userAction(userPoolId, username, 'unlock');
  }

  activateUser(userPoolId: string, username: string): Observable<any> {
    return this.userAction(userPoolId, username, 'activate');
  }

  deactivateUser(userPoolId: string, username: string): Observable<any> {
    return this.userAction(userPoolId, username, 'deactivate');
  }

  privacynoticeAccept(
    userPoolId: string,
    username: string
  ): Observable<any> {
    const url = this.api.adminUserAction;
    const params = {
      userPoolId: userPoolId,
      username: username,
      action: 'privacynoticeAccept',
      data: { MXVersion:''},
      uuxSessionId: this.storageService.getUUXSessionId()
    };
    return this.http.post(url, params);
  }

  privacynoticeDecline(
    userPoolId: string,
    username: string
  ): Observable<any> {
    const url = this.api.adminUserAction;
    const params = {
      userPoolId: userPoolId,
      username: username,
      action: 'privacynoticeDecline',
      data: { MXVersion: 'decline' },
      uuxSessionId: this.storageService.getUUXSessionId()
    };
    return this.http.post(url, params);
  }

  npsScorecard(
    userPoolId: string,
    username: string
  ): Observable<any> {
    const url = this.api.adminUserAction;
    const params = {
      userPoolId: userPoolId,
      username: username,
      action: 'npsScorecard',
      data: { },
      uuxSessionId: this.storageService.getUUXSessionId()
    };
    return this.http.post(url, params);
  }

  enableMFA(
    userPoolId: string,
    username: string,
    mfaPhone: string
  ): Observable<any> {
    const url = this.api.adminUserAction;
    const params = {
      userPoolId: userPoolId,
      username: username,
      action: 'enableMFA',
      data: { MFAPhone: mfaPhone },
      uuxSessionId: this.storageService.getUUXSessionId()
    };
    return this.http.post(url, params);
  }

  disableMFA(userPoolId: string, username: string): Observable<any> {
    return this.userAction(userPoolId, username, 'disableMFA');
  }

  userAction(
    userPoolId: string,
    username: string,
    action: string
  ): Observable<any> {
    const url = this.api.adminUserAction;
    const params = {
      userPoolId: userPoolId,
      username: username,
      action: action,
      uuxSessionId: this.storageService.getUUXSessionId()
    };
    this.log.trace('HsApiService.userAction.params', params);
    return this.http.post(url, params);
  }
  // -----------------

  saveUser(organization: Organization, user: AdminUser): Observable<any> {
    const url = this.api.adminUpdateUserDetails;
    const params = {
      userPoolId: organization.UserPoolId,
      organizationId: organization.OrganizationId,
      action: user.IsNew ? 'insert' : 'update',
      user: user,
      uuxSessionId: this.storageService.getUUXSessionId(),
      sendWelcome: user.IsSendInvite
    };
    this.log.trace('hsApi.saveUser', { url: url, params: params });
    return this.http.post(url, params);
  }

  getUrl(baseUrl: string, params?: any) {
    if (!params) {
      return baseUrl;
    } else {
      return baseUrl + '?' + this.encodeData(params);
    }
  }

  private encodeData(data: any): string {
    return Object.keys(data)
      .map(key => {
        return [key, data[key]].map(encodeURIComponent).join('=');
      })
      .join('&');
  }
}
